import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'utils/highchartsExporting';
import { getChartDataSettings } from '../../../components/partials/PivotChart/utils';
import Spinner from '../../../components/widgets/Spinner';
import CreativeChartHeader from './CreativeChartHeader';
import css from './CreativeChart.css';
import { COLUMN_CHART, LINE_CHART } from '../../../utils/dashboardConsts';
import { WIDGET_TYPE_TO_SCHEMA } from '../../../components/partials/ChartWidgetWrapper/consts';
import { EmptyState } from '../../../components/widgets';
import { BREAKDOWN_DIMENSIONS, GalleryContext } from '../../consts';
import { lineColors } from '../../../utils/charts';
import { SHOW_ALL } from '../../../components/partials/TopXChartDataFilter';

export const TOP_X_THRESHOLD = 5;
const BREAKDOWN_CHART_NO_DATA = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.ANALYSIS.BREAKDOWN_CHART_NO_DATA';
const PERFORMANCE_CHART_NO_DATA = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.ANALYSIS.PERFORMANCE_CHART_NO_DATA';

const hasSeriesData = series => {
    if (!series.length) return false;
    return series.some(
        s =>
            s.data?.some(value => {
                if (value == null) return false;
                if (typeof value === 'object') return value.y != null;
                return true;
            })
    );
};

const buildColumn = (fieldsMetadata, metricName, metricDisplayName) => {
    const metricMetaData = fieldsMetadata?.[metricName] || {};
    return {
        name: metricName,
        display_name: metricDisplayName,
        displayFormat: metricMetaData.display_format || { type: 'number' },
    };
};

const CHART_TYPE_CONFIG = {
    [COLUMN_CHART]: {
        xAxisLabelRotation: 0,
        emptyStateIcon: WIDGET_TYPE_TO_SCHEMA.column,
    },
    [LINE_CHART]: {
        xAxisLabelRotation: -45,
        emptyStateIcon: WIDGET_TYPE_TO_SCHEMA.line,
    },
};

const buildChartOptions = ({
    chartType,
    categories,
    series,
    yAxisList,
    config,
    isColumnChart,
    selectedTab,
}) => ({
    title: { text: null },
    chart: {
        type: chartType,
        height: 250,
        animation: false,
        zoomType: 'xy',
        resetZoomButton: {
            theme: {
                fill: '#FFFFFF99',
                stroke: '#334155',
                style: {
                    color: '#334155',
                    fontSize: '0.8rem',
                },
                states: {
                    hover: {
                        fill: '#334155',
                        style: {
                            color: 'white',
                        },
                    },
                },
            },
            position: {
                align: 'right',
                verticalAlign: 'top',
                x: -10,
                y: 10,
            },
        },
    },
    xAxis: {
        categories,
        title: isColumnChart && { text: selectedTab?.label },
        reversed: !isColumnChart,
        labels: {
            enabled: true,
            style: {
                fontSize: '11px',
            },
            rotation: config.xAxisLabelRotation,
        },
    },
    yAxis: yAxisList,
    legend: {
        enabled: yAxisList.length > 1 || series.length > 1,
        reversed: true,
        animation: false,
    },
    tooltip: {
        shared: false,
        animation: false,
        enabled: true,
    },
    series,
    plotOptions: {
        series: {
            animation: false,
            shadow: false,
        },
        line: {
            marker: { enabled: true, radius: 3 },
            lineWidth: 2,
        },
    },
    colors: lineColors,
    credits: { enabled: false },
    exporting: { enabled: false },
});

const CreativeChart = ({
    dropdownOptions = [],
    data = [],
    loading = false,
    title,
    chartType,
    tabs = [],
    selectedTab,
    onTabChange,
}) => {
    const { fieldsMetadata } = useContext(GalleryContext);
    const [selectedOption, setSelectedOption] = useState(dropdownOptions[0]);
    const [selectedTopX, setSelectedTopX] = useState(TOP_X_THRESHOLD);
    const chartRef = useRef(null);
    const dataLength = data?.length || 0;

    useEffect(() => {
        if (dropdownOptions.length && !selectedOption) setSelectedOption(dropdownOptions[0]);
    }, [dropdownOptions, selectedOption]);

    // Reset Top X to default when switching dimensions or when data length changes
    useEffect(() => {
        if (dataLength <= TOP_X_THRESHOLD) {
            setSelectedTopX(SHOW_ALL);
        } else {
            setSelectedTopX(TOP_X_THRESHOLD);
        }
    }, [selectedTab, dataLength]);

    // Reset chart zoom when switching metrics, Top X, or dimensions
    useEffect(() => {
        if (chartRef.current?.chart) {
            chartRef.current.chart.zoomOut();
        }
    }, [selectedOption, selectedTopX, selectedTab]);

    const isBreakdownChart = chartType === COLUMN_CHART;
    const isTopXDisabled = loading || dataLength <= TOP_X_THRESHOLD;

    const filteredData = useMemo(() => {
        if (!data?.length || !isBreakdownChart || !selectedOption) return data;

        const metricName = selectedOption.value;
        const sortedData = [...data].sort((a, b) => (b[metricName] || 0) - (a[metricName] || 0));

        if (selectedTopX === SHOW_ALL) return sortedData;

        return sortedData.slice(0, selectedTopX);
    }, [data, isBreakdownChart, selectedTopX, selectedOption]);

    const chartOptions = useMemo(() => {
        if (!filteredData?.length || !selectedOption) return null;

        const config = CHART_TYPE_CONFIG[chartType];
        const metricColumn = buildColumn(fieldsMetadata, selectedOption.value, selectedOption.label);

        const chartDataArgs =
            isBreakdownChart
                ? {
                      chartName: COLUMN_CHART,
                      metrics: [metricColumn],
                      dimensions: [buildColumn(fieldsMetadata, selectedTab?.value, selectedTab?.label)],
                  }
                : {
                      chartName: LINE_CHART,
                      metrics: [metricColumn],
                      dimensions: [buildColumn(fieldsMetadata, BREAKDOWN_DIMENSIONS.DATE.value, BREAKDOWN_DIMENSIONS.DATE.label)],
                  };

        const [categories, series, yAxisList] = getChartDataSettings(
            { name: chartDataArgs.chartName },
            chartDataArgs.metrics,
            chartDataArgs.dimensions,
            [],
            filteredData
        );

        if (!hasSeriesData(series)) return null;

        return buildChartOptions({
            chartType,
            categories,
            series,
            yAxisList,
            config,
            isColumnChart: isBreakdownChart,
            selectedTab,
        });
    }, [filteredData, selectedOption, chartType, selectedTab]);

    return (
        <div className={css.chartContainer}>
            <CreativeChartHeader
                title={title}
                dropdownOptions={dropdownOptions}
                selectedOption={selectedOption}
                onOptionChanged={setSelectedOption}
                tabs={tabs}
                selectedTab={selectedTab}
                onTabChange={onTabChange}
                onTopXChanged={setSelectedTopX}
                showTopX={isBreakdownChart}
                selectedTopX={selectedTopX}
                rowDataLength={dataLength}
                isTopXDisabled={isTopXDisabled}
            />
            <div className={css.chart}>
                {loading ? (
                    <Spinner className={css.spinnerContainer} show />
                ) : chartOptions ? (
                    <HighchartsReact
                        ref={chartRef}
                        key={chartType}
                        highcharts={Highcharts}
                        options={chartOptions}
                    />
                ) : (
                    <EmptyState
                        customIcon={CHART_TYPE_CONFIG[chartType]?.emptyStateIcon}
                        header={isBreakdownChart ? BREAKDOWN_CHART_NO_DATA : PERFORMANCE_CHART_NO_DATA}
                        containerClass={css.emptyStateContainer}
                        headerClassName={css.chartHeader}
                    />
                )}
            </div>
        </div>
    );
};

CreativeChart.propTypes = {
    dropdownOptions: PropTypes.arrayOf(PropTypes.object),
    data: PropTypes.array,
    loading: PropTypes.bool,
    title: PropTypes.string,
    chartType: PropTypes.string,
    tabs: PropTypes.array,
    selectedTab: PropTypes.object,
    onTabChange: PropTypes.func,
};

export default CreativeChart;
